import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { trainingService, type Lesson, type AdminLessonSummary } from '../../services/training.service';
import { useConfirm } from '../../contexts/ConfirmContext';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Status Badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'text-on-surface-variant bg-surface-container border-outline-variant/20',
    processing: 'text-tertiary bg-tertiary/10 border-tertiary/20',
    done: 'text-success bg-success/10 border-success/20',
    failed: 'text-error bg-error/10 border-error/20',
  };
  return (
    <span className={`inline-flex min-h-6 items-center justify-center rounded-full border px-2.5 text-[11px] font-semibold leading-none ${styles[status] || styles.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ─── Sortable Lesson Row ────────────────────────────────────────────────────

function SortableLessonRow({
  lesson,
  index,
  trainingId,
  uploadingLessonId,
  uploadStatus,
  onEdit,
  onDelete,
  onUpload,
}: {
  lesson: Lesson;
  index: number;
  trainingId: string;
  uploadingLessonId: string | null;
  uploadStatus: string | undefined;
  onEdit: (lessonId: string) => void;
  onDelete: (lessonId: string) => void;
  onUpload: (lessonId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lesson.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  const hasQuiz = lesson.quiz && Array.isArray(lesson.quiz) && lesson.quiz.length > 0;
  const isUploading = uploadingLessonId === lesson.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-[2.5rem_1fr_6rem_5rem_5rem_6rem_6rem] gap-4 px-5 py-3.5 border-b border-outline-variant/10 hover:bg-surface-container transition-colors items-center ${isDragging ? 'bg-surface-container-high shadow-md' : ''}`}
    >
      <div className="flex items-center gap-1">
        <button
          {...attributes}
          {...listeners}
          className="p-0.5 rounded hover:bg-surface-container-high text-on-surface-variant/40 hover:text-on-surface-variant cursor-grab active:cursor-grabbing"
        >
          <span className="material-symbols-outlined text-[16px]">drag_indicator</span>
        </button>
        <span className="text-sm text-on-surface-variant font-mono">{index + 1}</span>
      </div>

      <button
        onClick={() => onEdit(lesson.id)}
        className="text-left min-w-0"
      >
        <p className="text-sm font-semibold text-on-surface hover:text-primary transition-colors truncate">{lesson.title}</p>
        {lesson.description && <p className="text-[11px] text-on-surface-variant truncate">{lesson.description}</p>}
      </button>

      <div className="flex justify-center">
        {isUploading ? (
          <span className="material-symbols-outlined text-tertiary text-lg animate-spin" style={{ animationDuration: '2s' }}>sync</span>
        ) : (
          <StatusBadge status={lesson.status || 'pending'} />
        )}
      </div>

      <span className="text-sm text-on-surface-variant text-center">
        {lesson.duration > 0 ? `${Math.floor(lesson.duration / 60)}:${(lesson.duration % 60).toString().padStart(2, '0')}` : '—'}
      </span>

      <div className="flex justify-center">
        {hasQuiz ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success bg-success/10 px-2 py-0.5 rounded-full">
            <span className="material-symbols-outlined text-[14px]">check</span>
            {lesson.quiz!.length}
          </span>
        ) : (
          <span className="text-[11px] text-on-surface-variant/40">—</span>
        )}
      </div>

      <div className="flex justify-center">
        {lesson.status === 'done' || lesson.status === 'processing' ? (
          <span className="text-[11px] text-on-surface-variant/50">Uploaded</span>
        ) : (
          <button
            onClick={() => onUpload(lesson.id)}
            disabled={isUploading}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-surface-container text-[11px] font-medium text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition-colors disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-[14px]">upload</span>
            Upload
          </button>
        )}
      </div>

      <div className="flex justify-end gap-1">
        <button
          onClick={() => onEdit(lesson.id)}
          className="p-1.5 rounded-lg hover:bg-surface-container text-on-surface-variant hover:text-primary transition-colors"
          title="Edit"
        >
          <span className="material-symbols-outlined text-[18px]">edit</span>
        </button>
        <button
          onClick={() => onDelete(lesson.id)}
          className="p-1.5 rounded-lg hover:bg-error/10 text-on-surface-variant hover:text-error transition-colors"
          title="Delete"
        >
          <span className="material-symbols-outlined text-[18px]">delete</span>
        </button>
      </div>

      {uploadStatus && (
        <div className="col-span-full -mt-1 mb-0.5">
          <p className={`text-[11px] ${uploadStatus.startsWith('Error') ? 'text-error' : 'text-on-surface-variant'}`}>{uploadStatus}</p>
        </div>
      )}
    </div>
  );
}

// ─── Create Lesson Modal ────────────────────────────────────────────────────

function CreateLessonModal({
  open,
  onClose,
  onCreate,
  isPending,
  trainingId,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (trainingId: string, data: { title: string; description: string; order: number }) => void;
  isPending: boolean;
  trainingId: string;
}) {
  const [form, setForm] = useState({ title: '', description: '', order: 0 });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-surface-container-low rounded-2xl border border-outline-variant/15 p-6 shadow-xl space-y-4">
        <h2 className="text-lg font-bold text-on-surface">Add Video</h2>
        <input
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="Video title"
          className="w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors"
          required
        />
        <textarea
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Description (optional) — will be auto-generated by AI after upload"
          className="w-full bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors resize-none"
          rows={2}
        />
        <div className="flex gap-3 items-center">
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))}
            placeholder="Order"
            className="w-24 bg-surface-container rounded-xl px-4 py-3 text-sm text-on-surface placeholder:text-on-surface-variant/50 border border-outline-variant/20 focus:border-primary/40 focus:outline-none transition-colors"
          />
          <p className="text-xs text-on-surface-variant/60">
            Duration is auto-detected from the uploaded video.
          </p>
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={() => { onCreate(trainingId, form); setForm({ title: '', description: '', order: 0 }); }}
            disabled={!form.title.trim() || isPending}
            className="flex-1 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {isPending ? 'Adding...' : 'Add Video'}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-surface-container text-sm font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function AdminTrainingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadingLessonId, setUploadingLessonId] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploadLessonId, setPendingUploadLessonId] = useState<string | null>(null);

  const { data: training, isLoading } = useQuery({
    queryKey: ['admin-training', id],
    queryFn: () => trainingService.adminGet(id!),
    enabled: !!id,
  });

  const createMutation = useMutation({
    mutationFn: ({ trainingId, ...data }: { trainingId: string } & { title: string; description: string; order: number }) =>
      trainingService.adminCreateLesson(trainingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-training', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-trainings'] });
      setCreateOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: trainingService.adminDeleteLesson,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-training', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-trainings'] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: trainingService.adminReorderLessons,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-training', id] });
    },
  });

  async function handleFileSelect(file: File, lessonId: string) {
    setUploadingLessonId(lessonId);
    setUploadStatus((s) => ({ ...s, [lessonId]: 'Uploading video to Vimeo...' }));
    try {
      await trainingService.adminUploadVideo(lessonId, file);
      setUploadStatus((s) => ({ ...s, [lessonId]: 'Processing started!' }));
      queryClient.invalidateQueries({ queryKey: ['admin-training', id] });
    } catch (err) {
      setUploadStatus((s) => ({ ...s, [lessonId]: `Error: ${(err as Error).message}` }));
    } finally {
      setUploadingLessonId(null);
    }
  }

  const rawLessons = training?.lessons ?? [];
  const [lessons, setLessons] = useState<Lesson[]>([]);
  useEffect(() => {
    setLessons(rawLessons);
  }, [rawLessons]);

  const doneCount = lessons.filter((l) => l.status === 'done').length;
  const processingCount = lessons.filter((l) => l.status === 'processing').length;
  const failedCount = lessons.filter((l) => l.status === 'failed').length;
  const withQuizCount = lessons.filter((l) => l.quiz && Array.isArray(l.quiz) && l.quiz.length > 0).length;
  const thumbUrl = training?.thumbnailPresignedUrl || training?.thumbnailUrl;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = lessons.findIndex((l) => l.id === active.id);
    const newIndex = lessons.findIndex((l) => l.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const next = arrayMove(lessons, oldIndex, newIndex);
    setLessons(next);

    const orders = next.map((l, i) => ({ id: l.id, order: i }));
    reorderMutation.mutate(orders);
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8">
        <div className="h-6 w-40 bg-surface-container-high rounded animate-pulse" />
        <div className="h-8 w-72 bg-surface-container-high rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 bg-surface-container-high rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="h-96 bg-surface-container-high rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!training) {
    return (
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto">
        <div className="text-center py-20">
          <span className="material-symbols-outlined text-5xl text-on-surface-variant/30 block mb-3">error</span>
          <p className="text-on-surface font-semibold">Module not found</p>
          <button onClick={() => navigate('/admin/trainings')} className="mt-4 text-primary text-sm font-medium hover:underline">
            Back to modules
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8 md:space-y-10">
      {/* Breadcrumb + Header */}
      <div className="space-y-1">
        <button
          onClick={() => navigate('/admin/trainings')}
          className="flex items-center gap-1 text-xs text-on-surface-variant hover:text-on-surface transition-colors mb-2"
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          All Modules
        </button>

        <div className="flex items-start gap-5">
          {thumbUrl && (
            <div className="hidden sm:block w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-surface-container border border-outline-variant/15">
              <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-on-surface">{training.title}</h1>
            {training.description && <p className="text-on-surface-variant text-sm mt-1">{training.description}</p>}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-[11px] font-semibold text-on-surface-variant bg-surface-container px-2.5 py-1 rounded-full border border-outline-variant/15">
                {lessons.length} {lessons.length === 1 ? 'video' : 'videos'}
              </span>
              <span className="text-[11px] text-on-surface-variant/60">order {training.order}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface-container-low rounded-2xl border border-success/20 p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-success" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-on-surface-variant font-semibold uppercase tracking-wider mb-1">Ready</p>
              <p className="text-2xl font-extrabold text-on-surface">{doneCount}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-success/10">
              <span className="material-symbols-outlined text-success text-xl">check_circle</span>
            </div>
          </div>
        </div>
        <div className="bg-surface-container-low rounded-2xl border border-tertiary/20 p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-tertiary" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-on-surface-variant font-semibold uppercase tracking-wider mb-1">Processing</p>
              <p className="text-2xl font-extrabold text-on-surface">{processingCount}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-tertiary/10">
              <span className="material-symbols-outlined text-tertiary text-xl">sync</span>
            </div>
          </div>
        </div>
        <div className="bg-surface-container-low rounded-2xl border border-error/20 p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-error" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-on-surface-variant font-semibold uppercase tracking-wider mb-1">Failed</p>
              <p className="text-2xl font-extrabold text-on-surface">{failedCount}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-error/10">
              <span className="material-symbols-outlined text-error text-xl">error</span>
            </div>
          </div>
        </div>
        <div className="bg-surface-container-low rounded-2xl border border-primary/20 p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-on-surface-variant font-semibold uppercase tracking-wider mb-1">With Quiz</p>
              <p className="text-2xl font-extrabold text-on-surface">{withQuizCount}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <span className="material-symbols-outlined text-primary text-xl">quiz</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.15em] text-on-surface-variant font-semibold">Videos</h3>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all"
        >
          <span className="material-symbols-outlined text-lg">add</span>
          Add Video
        </button>
      </div>

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && pendingUploadLessonId) {
            handleFileSelect(file, pendingUploadLessonId);
            setPendingUploadLessonId(null);
          }
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
        className="hidden"
      />

      {/* Lessons Table */}
      {lessons.length === 0 ? (
        <div className="text-center py-20 bg-surface-container-low rounded-2xl border border-outline-variant/10">
          <span className="material-symbols-outlined text-6xl text-on-surface-variant/30 block mb-4">play_circle</span>
          <p className="text-on-surface font-semibold text-lg">No videos yet</p>
          <p className="text-on-surface-variant text-sm mt-1">Add your first video to this module.</p>
        </div>
      ) : (
        <div className="bg-surface-container-low rounded-2xl overflow-hidden border border-outline-variant/10">
          {/* Table Header */}
          <div className="grid grid-cols-[2.5rem_1fr_6rem_5rem_5rem_6rem_6rem] gap-4 px-5 py-3 border-b border-outline-variant/15 text-[11px] font-semibold text-on-surface-variant uppercase tracking-wider items-center">
            <span>#</span>
            <span>Video</span>
            <span className="text-center">Status</span>
            <span className="text-center">Duration</span>
            <span className="text-center">Quiz</span>
            <span className="text-center">Upload</span>
            <span className="text-right">Actions</span>
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={lessons.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              {lessons.map((lesson, idx) => (
                <SortableLessonRow
                  key={lesson.id}
                  lesson={lesson}
                  index={idx}
                  trainingId={training.id}
                  uploadingLessonId={uploadingLessonId}
                  uploadStatus={uploadStatus[lesson.id]}
                  onEdit={(lessonId) => navigate(`/admin/trainings/${training.id}/lessons/${lessonId}`)}
                  onDelete={async (lessonId) => {
                    if (
                      await confirm({
                        title: 'Delete this video?',
                        message: 'This lesson and its uploaded video will be removed. This cannot be undone.',
                        confirmLabel: 'Delete',
                        danger: true,
                      })
                    ) {
                      deleteMutation.mutate(lessonId);
                    }
                  }}
                  onUpload={(lessonId) => {
                    setPendingUploadLessonId(lessonId);
                    fileInputRef.current?.click();
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <CreateLessonModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={(trainingId, data) => createMutation.mutate({ trainingId, ...data })}
        isPending={createMutation.isPending}
        trainingId={training.id}
      />
    </div>
  );
}
